import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { AuthSessionService } from '../../../services/auth-session.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ApiUrl } from '../../../shared/models/apiurl.models';
import { SystemApp } from '../../../constans/SystemConfigurations';
import { throwError, Subject } from 'rxjs';
import { catchError, map, debounceTime, distinctUntilChanged, takeUntil, finalize } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { LoadingMaskServiceService } from '../../../components/loading-frame/loading-frame.component';
import { Router } from '@angular/router';
import { LoginAction, LoginService } from '../../../components/session/login/login.service';
import { UserAds, StatusAds } from '../../../models/user-ads';
import { MatDialog } from '@angular/material/dialog';
import { AdsPageComponentDialog } from '../ads/ads-page/ads-page-component-dialog.component';
import { AdsService } from '../ads/services/ads.service';
import { QuestionService } from '../../create-adv/services/question.service';
import { PromoteAdDialogComponent } from '../../../components/promote-ad-dialog/promote-ad-dialog.component';

interface UserInfo {
  first_name: string;
  last_name:  string;
  mainPhone:  string;
  avatar:     string;
}

type SortKey = 'newest' | 'oldest' | 'price_asc' | 'price_desc';
type ViewMode = 'grid' | 'list';

@Component({
    selector: 'app-my-ads',
    templateUrl: './my-ads.component.html',
    styleUrls: ['./my-ads.component.css'],
    standalone: false
})
export class MyAdsComponent implements OnInit, OnDestroy {
  private readonly authSession = inject(AuthSessionService);
  private readonly questionService = inject(QuestionService);

  // @ts-ignore
  userInfo: UserInfo = {};
  ads:         UserAds[] = [];
  filteredAds: UserAds[] = [];
  loading        = false;
  selectedFilter = 'all';
  base_url: string;

  readonly StatusAds = StatusAds;

  // built dynamically once ads load
  filters:       { key: string; label: string; count: number }[]              = [];
  statusFilters: { key: string; label: string; cls: string; count: number }[] = [];

  selectedStatusFilter = 'Active';
  filterOpen  = false;
  searchQuery = '';
  sortKey:  SortKey  = 'newest';
  viewMode: ViewMode = 'grid';

  // stats
  stats = { total: 0, active: 0, inReview: 0, paused: 0 };

  // pending confirmations
  pendingDeleteId: number | null = null;
  pendingPauseId:  number | null = null;

  // Server refusal of a quick action (e.g. resuming a paused ad past the
  // free limit / VIP quota: FREE_PLAN_LIMIT_REACHED, VIP_QUOTA_REACHED,
  // PLAN_RENEWAL_REQUIRED). Shown above the list; cleared on the next action.
  actionError = '';
  actionErrorCode = '';

  /** Refusals the plans page can solve (renew / upgrade). */
  get actionErrorNeedsPlan(): boolean {
    return this.actionErrorCode === 'VIP_QUOTA_REACHED' || this.actionErrorCode === 'PLAN_RENEWAL_REQUIRED';
  }

  private searchSubject = new Subject<string>();
  private destroy$      = new Subject<void>();

  // ── master lookup maps ────────────────────────────────────────
  private readonly TYPE_MAP: Record<string, string> = {
    car:      'רכב',
    nadlan:   'נדל"ן',
    sale2:    'יד שניה',
    required: 'דרושים',
    coworker: 'בעלי מקצוע',
    pet:      'חיות מחמד',
    teacher:  'מורים פרטיים',
  };

  private readonly STATUS_MAP: Record<string, { label: string; cls: string }> = {
    Active:     { label: 'פעיל',         cls: 'sf-active'  },
    Done:       { label: 'הושלם',        cls: 'sf-active'  },
    inReview:   { label: 'בבדיקה',       cls: 'sf-pending' },
    Pause:      { label: 'מושהה',        cls: 'sf-pending' },
    PayPending: { label: 'ממתין לתשלום', cls: 'sf-pending' },
    Delete:     { label: 'נמחק',         cls: 'sf-inactive'},
    Blocked:    { label: 'חסום',         cls: 'sf-inactive'},
    NeedsFix:   { label: 'ממתין לתיקון', cls: 'sf-pending' },
    Cancelled:  { label: 'בוטל',         cls: 'sf-inactive'},
  };

  /** Coupon epic: shown on a Cancelled ad (no owner actions on it). */
  readonly cancelledExplanation = 'המודעה בוטלה יחד עם ההטבה שמימנה אותה';

  /** NeedsFix ad currently being re-sent for review (no double submit). */
  resubmittingId: number | null = null;

  readonly sortOptions: { key: SortKey; label: string }[] = [
    { key: 'newest',     label: 'חדש ביותר'  },
    { key: 'oldest',     label: 'ישן ביותר'  },
    { key: 'price_desc', label: 'מחיר ↓'     },
    { key: 'price_asc',  label: 'מחיר ↑'     },
  ];

  private get options() {
    return {
      headers: new HttpHeaders({
        'Content-Type':  'application/json',
        'Authorization': this.apiKey.getJWTToken()
      })
    };
  }

  constructor(
    private http:           HttpClient,
    private apiKey:         ApiUrl,
    private loadingService: LoadingMaskServiceService,
    public  translate:      TranslateService,
    private router:         Router,
    private loginService:   LoginService,
    private dialog:         MatDialog,
    private adsService:     AdsService
  ) {
    this.base_url = SystemApp.base_url_api;
  }

  ngOnInit(): void {
    this.loadUserInfo();
    this.loadAds();

    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(q => {
      this.searchQuery = q;
      this.applyFilter();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── data loaders ──────────────────────────────────────────────
  private loadUserInfo(): void {
    this.http.get(this.apiKey.getUserInfo(), this.options)
      .pipe(map(r => r ?? null), catchError(e => throwError(e.error || e)))
      .subscribe({ next: (res: any) => { this.userInfo = res; }, error: () => {} });
  }

  loadAds(): void {
    this.loading = true;
    this.loadingService.setOnLoadingMask(true, true);
    this.http.get<UserAds[]>(this.apiKey.getAds(), this.options)
      .pipe(catchError(e => throwError(e.error || e)))
      .subscribe({
        next: res => {
          this.ads = res || [];
          this.buildStats();
          this.buildFilters();
          this.applyFilter();
          this.loading = false;
          this.loadingService.setOnLoadingMask(false, false);
          this.prefetchAdForms();
        },
        error: () => {
          this.loading = false;
          this.loadingService.setOnLoadingMask(false, false);
        }
      });
  }

  // ── stats ─────────────────────────────────────────────────────
  private buildStats(): void {
    this.stats = {
      total:    this.ads.length,
      active:   this.ads.filter(a => a.statusAds === StatusAds.Active || a.statusAds === StatusAds.Done).length,
      inReview: this.ads.filter(a => a.statusAds === StatusAds.inReview || a.statusAds === StatusAds.PayPending).length,
      paused:   this.ads.filter(a => a.statusAds === StatusAds.Pause).length,
    };
  }

  // ── dynamic filters ───────────────────────────────────────────
  private buildFilters(): void {
    const types = [...new Set(this.ads.map(a => a.resourcetype).filter(Boolean))];
    this.filters = [
      { key: 'all', label: 'הכל', count: this.ads.length },
      ...types.map(t => ({
        key:   t,
        label: this.TYPE_MAP[t.toLowerCase()] ?? t,
        count: this.ads.filter(a => a.resourcetype?.toLowerCase() === t.toLowerCase()).length,
      }))
    ];

    const statuses = [...new Set(this.ads.map(a => a.statusAds).filter(Boolean))];
    this.statusFilters = [
      { key: 'all', label: 'הכל', cls: 'sf-all', count: this.ads.length },
      ...statuses.map(s => ({
        key:   s,
        label: this.STATUS_MAP[s]?.label ?? s,
        cls:   this.STATUS_MAP[s]?.cls   ?? 'sf-all',
        count: this.ads.filter(a => a.statusAds === s).length,
      }))
    ];
  }

  // ── filter + sort ─────────────────────────────────────────────
  applyFilter(typeKey: string = this.selectedFilter, statusKey: string = this.selectedStatusFilter): void {
    this.selectedFilter       = typeKey;
    this.selectedStatusFilter = statusKey;
    const q = this.searchQuery.toLowerCase();

    let result = this.ads.filter(a => {
      const typeMatch   = typeKey   === 'all' || a.resourcetype?.toLowerCase() === typeKey.toLowerCase();
      const statusMatch = statusKey === 'all' || a.statusAds === statusKey;
      const searchMatch = !q || [a.name, a.description, String(a.price ?? ''), this.typeLabel(a.resourcetype)]
        .some(f => f?.toLowerCase().includes(q));
      return typeMatch && statusMatch && searchMatch;
    });

    // sort
    result = [...result].sort((a, b) => {
      if (this.sortKey === 'newest')     return new Date(b.createTime).getTime() - new Date(a.createTime).getTime();
      if (this.sortKey === 'oldest')     return new Date(a.createTime).getTime() - new Date(b.createTime).getTime();
      if (this.sortKey === 'price_desc') return (b.price ?? 0) - (a.price ?? 0);
      if (this.sortKey === 'price_asc')  return (a.price ?? 0) - (b.price ?? 0);
      return 0;
    });

    this.filteredAds = result;
  }

  applyStatusFilter(key: string): void  { this.applyFilter(this.selectedFilter, key); }
  applySort(key: SortKey): void         { this.sortKey = key; this.applyFilter(); }
  setView(mode: ViewMode): void         { this.viewMode = mode; }

  // ── search ────────────────────────────────────────────────────
  onSearch(value: string): void  { this.searchSubject.next(value.trim()); }
  clearSearch(): void            { this.searchQuery = ''; this.searchSubject.next(''); }

  // ── quick actions on card ─────────────────────────────────────
  quickAction(ad: UserAds, action: 'play' | 'pause' | 'delete', event: Event): void {
    event.stopPropagation();

    // actions that require confirmation
    if (action === 'delete') {
      this.pendingDeleteId = ad['id'];
      this.pendingPauseId  = null;
      return;
    }
    if (action === 'pause') {
      this.pendingPauseId  = ad['id'];
      this.pendingDeleteId = null;
      return;
    }

    this.executeAction(ad, action);
  }

  confirmDelete(ad: UserAds, event: Event): void {
    event.stopPropagation();
    this.pendingDeleteId = null;
    this.executeAction(ad, 'delete');
  }

  cancelDelete(event: Event): void {
    event.stopPropagation();
    this.pendingDeleteId = null;
  }

  confirmPause(ad: UserAds, event: Event): void {
    event.stopPropagation();
    this.pendingPauseId = null;
    this.executeAction(ad, 'pause');
  }

  cancelPause(event: Event): void {
    event.stopPropagation();
    this.pendingPauseId = null;
  }

  /**
   * NeedsFix -> back to admin review (no new unit). The owner edits the ad
   * from the details dialog first, as usual.
   */
  resubmit(ad: UserAds, event: Event): void {
    event.stopPropagation();
    if (this.resubmittingId !== null || !this.canResubmit(ad)) return;
    this.executeAction(ad, 'resubmit');
  }

  private executeAction(ad: UserAds, action: 'play' | 'pause' | 'delete' | 'resubmit'): void {
    const payloads: Record<string, any> = {
      play:     { resourcetype: ad.resourcetype, statusAds: 'Active', active: true },
      pause:    { resourcetype: ad.resourcetype, statusAds: 'Pause' },
      delete:   { resourcetype: ad.resourcetype, statusAds: 'Delete' },
      resubmit: { resourcetype: ad.resourcetype, statusAds: 'inReview' },
    };
    this.actionError = '';
    this.actionErrorCode = '';
    if (action === 'resubmit') this.resubmittingId = ad['id'];
    this.adsService.updateAds(ad['id'], payloads[action]).pipe(
      finalize(() => { if (action === 'resubmit') this.resubmittingId = null; })
    ).subscribe({
      next: (res: any) => {
        this.ads = this.ads.map(a => a['id'] === res['id'] ? res : a);
        this.buildStats();
        this.buildFilters();
        this.applyFilter();
      },
      error: (err: any) => {
        this.actionError = err?.error?.message || 'הפעולה נכשלה, נסו שוב.';
        this.actionErrorCode = err?.error?.error || '';
      },
    });
  }

  // SCRUM-177: only a paused (already approved) ad can be resumed by its
  // owner. An ad in review goes live only when an admin approves it.
  // 2026-10-23: resuming sends the ad back to review (server stores
  // inReview), and an ad an admin froze (quota_released) can't be resumed.
  canPlay(ad: UserAds):   boolean {
    return ad.statusAds === StatusAds.Pause && !(ad as any)['quota_released'];
  }
  /** Paused by an admin - shown instead of the play button. */
  isAdminFrozen(ad: UserAds): boolean {
    return ad.statusAds === StatusAds.Pause && !!(ad as any)['quota_released'];
  }
  canPause(ad: UserAds):  boolean { return ad.statusAds === StatusAds.Active; }
  canDelete(ad: UserAds): boolean {
    return ad.statusAds !== StatusAds.Delete && ad.statusAds !== StatusAds.Done && ad.statusAds !== StatusAds.Cancelled;
  }

  /** Cancelled with the coupon that funded it - the owner can do nothing with it. */
  isCancelled(ad: UserAds): boolean { return ad.statusAds === StatusAds.Cancelled; }
  /** Returned by an admin for correction - edit, then send back to review. */
  canResubmit(ad: UserAds): boolean { return ad.statusAds === StatusAds.NeedsFix; }
  isCouponAd(ad: UserAds): boolean { return ad.quota_source === 'COUPON'; }

  /** SCRUM-266 — any live listing can be promoted after it was published. */
  canPromote(ad: UserAds): boolean { return ad.statusAds === StatusAds.Active; }

  openPromote(ad: UserAds, event: Event): void {
    event.stopPropagation();
    this.dialog
      .open(PromoteAdDialogComponent, {
        data: { adId: ad['id'], adName: (ad as any)['name'] || '' },
        direction: 'rtl',
        autoFocus: false,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result === 'promoted') this.loadAds();
      });
  }

  /**
   * SCRUM-147: warm the (cached) form schema of every category the user has
   * ads in, in the background, so opening an ad's details is instant instead
   * of waiting on "טוען פרטי מודעה...". Same cache key as the dialog
   * (lower-cased namespace, no sub-category). Failures are ignored - the
   * dialog simply fetches again.
   */
  private prefetchAdForms(): void {
    const namespaces = new Set(
      this.ads.map(a => (a.resourcetype || 'Car').toLowerCase()).filter(Boolean)
    );
    namespaces.forEach(ns =>
      this.questionService.getServerQuestions(ns).subscribe({ error: () => {} })
    );
  }

  // ── dialog ────────────────────────────────────────────────────
  openAdDialog(ad: UserAds): void {
    const ref = this.dialog.open(AdsPageComponentDialog, {
      data: { selectAds: ad, namespace: ad.resourcetype || 'Car' },
      width: '90vw', maxWidth: '1100px',
      panelClass: 'ad-detail-dialog', direction: 'rtl'
    });
    ref.afterClosed().subscribe((updatedAd: UserAds | undefined) => {
      if (updatedAd) {
        this.ads = this.ads.map(a => a['id'] === updatedAd['id'] ? updatedAd : a);
        this.buildStats();
        this.buildFilters();
        this.applyFilter();
      }
    });
  }

  // ── display helpers ───────────────────────────────────────────
  statusLabel(s: StatusAds): string {
    const m: Partial<Record<StatusAds, string>> = {
      [StatusAds.Active]:     'פעיל',
      [StatusAds.Done]:       'הושלם',
      [StatusAds.Delete]:     'נמחק',
      [StatusAds.inReview]:   'בבדיקה',
      [StatusAds.Pause]:      'מושהה',
      [StatusAds.PayPending]: 'ממתין לתשלום',
      [StatusAds.Blocked]:    'חסום',
      [StatusAds.NeedsFix]:   'ממתין לתיקון',
      [StatusAds.Cancelled]:  'בוטל',
    };
    return m[s] ?? s;
  }

  statusClass(s: StatusAds): string {
    if (s === StatusAds.Active || s === StatusAds.Done)                                    return 'badge-active';
    if (s === StatusAds.inReview || s === StatusAds.Pause || s === StatusAds.PayPending)   return 'badge-pending';
    if (s === StatusAds.NeedsFix)                                                          return 'badge-pending';
    return 'badge-inactive';
  }

  typeIcon(r: string): string {
    const t = (r || '').toLowerCase();
    if (t === 'car')      return 'fa-car-alt';
    if (t === 'nadlan')   return 'fa-building';
    if (t === 'required') return 'fa-briefcase';
    if (t === 'coworker') return 'fa-tools';
    if (t === 'pet')      return 'fa-paw';
    if (t === 'teacher')  return 'fa-graduation-cap';
    return 'fa-tag';
  }

  typeLabel(r: string): string {
    const t = (r || '').toLowerCase();
    if (t === 'car')      return 'רכב';
    if (t === 'nadlan')   return 'נדל"ן';
    if (t === 'sale2')    return 'יד שניה';
    if (t === 'required') return 'דרושים';
    if (t === 'coworker') return 'בעלי מקצוע';
    if (t === 'pet')      return 'חיות מחמד';
    if (t === 'teacher')  return 'מורים פרטיים';
    return 'מכירה';
  }

  activeTypeLabel():   string { return this.filters.find(f => f.key === this.selectedFilter)?.label       ?? ''; }
  activeStatusLabel(): string { return this.statusFilters.find(s => s.key === this.selectedStatusFilter)?.label ?? ''; }

  timeAgo(dateStr: string): string {
    if (!dateStr) return '';
    const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
    if (diff < 60)       return 'עכשיו';
    if (diff < 3600)     return `לפני ${Math.floor(diff / 60)} דק'`;
    if (diff < 86400)    return `לפני ${Math.floor(diff / 3600)} שע'`;
    if (diff < 2592000)  return `לפני ${Math.floor(diff / 86400)} ימים`;
    if (diff < 31536000) return `לפני ${Math.floor(diff / 2592000)} חודשים`;
    return `לפני ${Math.floor(diff / 31536000)} שנים`;
  }

  emptyStateMsg(): { icon: string; title: string; sub: string; showCta: boolean } {
    if (this.searchQuery) return {
      icon: 'mdi-magnify-close', title: `לא נמצאו תוצאות עבור "${this.searchQuery}"`,
      sub: 'נסה מילת חיפוש אחרת', showCta: false
    };
    if (this.selectedStatusFilter === StatusAds.inReview) return {
      icon: 'mdi-check-circle-outline', title: 'אין מודעות בבדיקה',
      sub: 'כל המודעות שלך אושרו ✓', showCta: false
    };
    if (this.selectedStatusFilter === StatusAds.Pause) return {
      icon: 'mdi-pause-circle-outline', title: 'אין מודעות מושהות',
      sub: 'כל המודעות שלך פעילות', showCta: false
    };
    if (this.selectedStatusFilter === StatusAds.Delete) return {
      icon: 'mdi-delete-outline', title: 'אין מודעות שנמחקו',
      sub: 'לא מחקת מודעות', showCta: false
    };
    if (this.selectedFilter !== 'all') return {
      icon: 'mdi-bulletin-board', title: `אין מודעות מסוג ${this.activeTypeLabel()}`,
      sub: 'פרסם מודעה חדשה בקטגוריה זו', showCta: true
    };
    return {
      icon: 'mdi-bulletin-board', title: 'עדיין לא פרסמת מודעות',
      sub: 'התחל לפרסם ותגיע לקהל רחב', showCta: true
    };
  }

  imgUrl(ad: UserAds): string {
    if (ad.images?.length) {
      const img = ad.images[0].image;
      return img.startsWith('http') ? img : this.base_url + img;
    }
    return 'assets/imgs/placeholder.png';
  }

  logout(): void {
    // POD1-7: full logout - revoke the refresh token server-side and drop
    // the socket, not just clear the local flag.
    this.authSession.logout().subscribe();
    this.loginService.sendClickEvent(LoginAction.Close);
  }
}
